import { memo, useMemo } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { CircularLoading } from "@/components/common";
import CheckoutForm from "@/pages/register/components/CheckoutForm";
import { useOrganizationContext } from "@/contexts/OrganizationContext";

import { useCrmSetupMetaExtensionPlan } from "../../hooks/useCrmSetupMetaExtensionPlan";

/**
 * Layout espelhando `PaymentPostAttendance` (resumo + CheckoutForm) para quem escolheu Meta no setup.
 */
const CrmSetupContractMetaBillingSection = memo(() => {
  const { organization } = useOrganizationContext();
  const { plan, isLoading, errorMessage } = useCrmSetupMetaExtensionPlan(true);

  const formatCurrency = useMemo(
    () => (value: number) =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value),
    [],
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" py={4} spacing={2}>
        <CircularLoading />
        <Typography variant="body2" color="text.secondary">
          Carregando informações do plano...
        </Typography>
      </Stack>
    );
  }

  if (errorMessage || !plan) {
    return (
      <Stack alignItems="center" py={3} spacing={1.5} px={1}>
        <Typography variant="subtitle1" color="error" fontWeight={600}>
          Não foi possível carregar o plano para assinatura
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {errorMessage ??
            "Tente novamente mais tarde ou fale com o suporte se o problema continuar."}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 0.5 }}
      >
        Para ativar a extensão FluxZap e vincular sua conta Meta / WhatsApp
        Business, conclua o pagamento da assinatura abaixo.
      </Typography>

      <Box sx={{ width: "100%", mt: 1 }}>
        <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{ p: 2.5, borderRadius: 2, height: "100%" }}
            >
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight={700}
                mb={2}
                align="center"
              >
                Resumo do plano
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Serviço
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    textAlign="right"
                  >
                    {plan.reason || "FluxZap (pós-atendimento)"}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Valor mensal
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrency(plan.auto_recurring.transaction_amount)}
                  </Typography>
                </Stack>
                {plan.auto_recurring.billing_day ? (
                  <>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Cobrança
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        Dia {plan.auto_recurring.billing_day}
                      </Typography>
                    </Stack>
                  </>
                ) : null}
                <Divider />
                <Stack direction="row" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Organização
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    textAlign="right"
                  >
                    {organization?.name ?? "Sua organização"}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
              <CheckoutForm
                selectedPlan={plan}
                enableCrm20TrialOnSuccess
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
});

CrmSetupContractMetaBillingSection.displayName =
  "CrmSetupContractMetaBillingSection";

export default CrmSetupContractMetaBillingSection;
